/**
 * SpoilerPlugin — handles cursor escape and cleanup for inline SpoilerNode.
 *
 * Without this, the cursor gets trapped inside the spoiler element.
 * Pressing arrow keys at the boundaries or typing at the edges
 * will always stay inside the spoiler.
 *
 * This plugin:
 * - Right arrow at end of spoiler → moves cursor after the spoiler
 * - Left arrow at start of spoiler → moves cursor before the spoiler
 * - Auto-removes empty SpoilerNodes (e.g. after delete/backspace)
 */

import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  $isTextNode,
  $createTextNode,
  KEY_ARROW_RIGHT_COMMAND,
  KEY_ARROW_LEFT_COMMAND,
  COMMAND_PRIORITY_HIGH,
} from 'lexical';
import { SpoilerNode, $isSpoilerNode } from '../nodes';

export function SpoilerPlugin(): null {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const unregisterRight = editor.registerCommand(
      KEY_ARROW_RIGHT_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return false;
        }

        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();
        const parent = anchorNode.getParent();

        if (!$isSpoilerNode(parent)) return false;

        // Check if cursor is at the very end of the spoiler's content
        const lastChild = parent.getLastChild();
        if (
          anchorNode === lastChild &&
          $isTextNode(anchorNode) &&
          anchor.offset === anchorNode.getTextContentSize()
        ) {
          event.preventDefault();
          // Insert an empty text node after the spoiler and select it
          const nextSibling = parent.getNextSibling();
          if (nextSibling && $isTextNode(nextSibling)) {
            nextSibling.select(0, 0);
          } else {
            const textNode = $createTextNode('');
            parent.insertAfter(textNode);
            textNode.select();
          }
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    const unregisterLeft = editor.registerCommand(
      KEY_ARROW_LEFT_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection) || !selection.isCollapsed()) {
          return false;
        }

        const anchor = selection.anchor;
        const anchorNode = anchor.getNode();
        const parent = anchorNode.getParent();

        if (!$isSpoilerNode(parent)) return false;

        // Check if cursor is at the very start of the spoiler's content
        const firstChild = parent.getFirstChild();
        if (
          anchorNode === firstChild &&
          $isTextNode(anchorNode) &&
          anchor.offset === 0
        ) {
          event.preventDefault();
          const prevSibling = parent.getPreviousSibling();
          if (prevSibling && $isTextNode(prevSibling)) {
            prevSibling.select();
          } else {
            const textNode = $createTextNode('');
            parent.insertBefore(textNode);
            textNode.select();
          }
          return true;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH,
    );

    // Auto-remove empty SpoilerNodes (e.g. after user deletes all content)
    const unregisterTransform = editor.registerNodeTransform(
      SpoilerNode,
      (node) => {
        if (node.getChildrenSize() === 0) {
          node.remove();
        }
      },
    );

    return () => {
      unregisterRight();
      unregisterLeft();
      unregisterTransform();
    };
  }, [editor]);

  return null;
}
